import React, { useCallback, useEffect, useState } from 'react'
import { Translate } from 'react-localize-redux'
import { IAccessScope } from 'reducers/Reducers.interfaces'
import { BasePathConst } from 'configs/Path.configs'
import {
  Card,
  CardContainer,
  CardContent,
  CardHeader,
  CardSizeTypes,
  Icon,
  SideRail,
  Spinner,
  SpinnerSizeTypes,
  Tag,
  TagType,
  Tooltip
} from '@digicert/dcone-common-ui'
import { faPencil } from '@digicert/dcone-common-ui/src/components/icons/light'
import { translate } from '../../../shared/helpers/utils'
import styles from './SignInSettings.module.scss'
import history from '../../../core/history/history'
import { ENABLED } from '../../../core/constants'
import { EditingTypes } from '../../tenants/tenant/signin-methods/SignInMethods.types'
import cn from 'classnames'
import SsoConfig from '../../../shared/components/sso-config/SsoConfig'
import { IActionCreatorAsync } from '../../../core/actions/Actions.interfaces'
import { fetchTenantById } from '../../tenants/Tenants.actions'
import {SignInSettingsProps} from "./SignInSettings.types";
import {connect} from "react-redux";
import OidcConfig from 'shared/components/oidc-config/OidcConfig'
import TwoFactorAuthentication from 'shared/components/two-factor-auth/TwoFactorAuthentication'

const SignInSettings = (
  {
    accountDetailsPage,
    tenant,
    fromSettings,
    canEdit,
    accessScope,
    primaryAccountId,
    fetchTenantById
  }:SignInSettingsProps) => {
  const [standardSignInMethod, setStandardSignInMethod] = useState(tenant.sign_in_methods.find(item => item.signInMethod==='standard'))
  const ssoMethodSaml = tenant.sign_in_methods.find(item => item.signInMethod==='singleSignOnSaml')
  const ssoMethodOidc = tenant.sign_in_methods.find(item => item.signInMethod==='singleSignOnOidc')
  const clientAuthCertLoginEnabled = tenant && tenant.sign_in_methods[0].clientAuthCertLoginEnabled
  //mfa can be edited only for self server accounts and sso only accounts
  const canEditMfa = tenant.can_edit_mfa
  const [showSideRail, setShowSideRail] = useState(false)
  const [sideRailContent, setSideRailContent] = useState('')
  const [ssoSamlEnabled, setSsoSamlEnabled] = useState(ssoMethodSaml?.status===ENABLED)
  const [oidcEnabled, setOidcEnabled] = useState(ssoMethodOidc?.status === ENABLED)
  const [account, setAccount] = useState(tenant)

  useEffect(() => {
    setSsoSamlEnabled(account?.sign_in_methods.find(item => item.signInMethod === 'singleSignOnSaml')?.status === ENABLED)
    setOidcEnabled(account?.sign_in_methods.find(item => item.signInMethod === 'singleSignOnOidc')?.status === ENABLED)
  }, [account])

  const editSSO = () => {
    setShowSideRail(true)
    setSideRailContent(EditingTypes.SSO_SAML)
  }

  const updateSignInMethods = useCallback(
    () => {
      fetchTenantById(primaryAccountId).then((response) => {
        setAccount({ ...response })
        setStandardSignInMethod(response.sign_in_methods.find(item => item.signInMethod==='standard'))
      })
      setShowSideRail(false)
    }, []
  )

  const loadSideRailTitle = useCallback(
    (sideRailContent: string) => {
      switch (sideRailContent) {
        case EditingTypes.SSO_SAML:
          return translate('ssoSaml.singleSignOnWithSaml')
        case EditingTypes.OIDC:
          return translate('common.form.singleSignOnOidc')
        case EditingTypes.TFA:
          return translate('editStandardSignIn.TFAHeadline')
      }
    }, [sideRailContent]
  )

  const loadSideRailContent = useCallback(
    (sideRailContent: string) => {
      switch (sideRailContent) {
        case EditingTypes.SSO_SAML:
          return <SsoConfig account={account} onCancel={() => setShowSideRail(false)} ssoSaml={ssoSamlEnabled} onChange={updateSignInMethods} primaryAccountId={primaryAccountId}/>
        case EditingTypes.OIDC:
            return <OidcConfig  onCancel={() => setShowSideRail(false)} ssoOidc={oidcEnabled} onChange={updateSignInMethods} account={account} setOidcEnabled={setOidcEnabled} primaryAccountId={primaryAccountId}/>
        case EditingTypes.TFA:
            return <TwoFactorAuthentication onCancel={() => setShowSideRail(false)} account={account} updateDetails={updateSignInMethods} />
        default:
          return <Spinner size={SpinnerSizeTypes.SMALL}/>
      }
    }, [sideRailContent, ssoSamlEnabled, account]
  )

  const handleActionEditSignIn = () => {
    history.push(`${BasePathConst}${fromSettings ? '/settings/':'/accounts/'}${tenant.id}/editStandardSignIn`)
  };

  const handleActionCreateAccountSignIn = () => {
    history.push(`${BasePathConst}${fromSettings ? '/settings/':'/accounts/'}${tenant.id}/createAccountSignIn`)
  };

  const handleActionEditMfa = () => {
    setShowSideRail(true)
    setSideRailContent(EditingTypes.TFA)
  };

  const handleActionOIDC = () => {
    setShowSideRail(true)
    setSideRailContent(EditingTypes.OIDC)
  };

  const handleActionUpdateClientAuthCertLogIn = () => {
    history.push(`${BasePathConst}${fromSettings ? '/settings/':'/accounts/'}${tenant.id}/updateClientAuthCertLogin`)
  };

  const oidcChange = useCallback(
    () => {
      setShowSideRail(false)
    }, []
  )

  return (
    <div className={styles.signInSettings}>
      <CardContainer>
        <>
          <Card
            size={CardSizeTypes.LARGE}
            header={
              <CardHeader
                label={translate('common.form.standard') as string}
                actions={[
                  {
                    icon: canEdit ?
                      <Tooltip title={translate('common.edit')}><Icon icon={faPencil}/></Tooltip>:undefined,
                    label: translate('common.edit'),
                    onClick: canEdit ? handleActionEditSignIn:undefined
                  }
                ]}
              />
            }
            content={
              <CardContent>
                <Translate id='common.form.standardDesc'/>
                <div className={styles.status}>
                  <p><Translate id='common.status'/></p>
                  <Tag type={standardSignInMethod?.status==ENABLED ? TagType.ACTIVE:TagType.INACTIVE}><Translate
                    id={`common.statuses.${standardSignInMethod?.status}`}/></Tag>
                </div>
              </CardContent>
            }
          />

          <Card
            size={CardSizeTypes.LARGE}
            header={
              <CardHeader
                label={translate('common.form.signInMethodTFA') as string}
                actions={
                  canEdit && canEditMfa && accessScope===IAccessScope.system ? [{
                      icon: <Tooltip title={translate('common.edit')}><Icon icon={faPencil}/></Tooltip>,
                      label: translate('common.edit'),
                      onClick: handleActionEditMfa
                    }]
                    :undefined
                }
              />
            }
            content={
              <CardContent>
                <Translate id='singleSignOn.mfaDesc'/>
                <div className={styles.status}>
                  <p><Translate id='common.status'/></p>
                  <Tag type={standardSignInMethod?.mfaStatus==ENABLED ? TagType.ACTIVE:TagType.INACTIVE}><Translate
                    id={`common.statuses.${standardSignInMethod?.mfaStatus}`}/></Tag>
                </div>
              </CardContent>
            }
          />
          <Card
            size={CardSizeTypes.LARGE}
            header={
              <CardHeader
                label={translate('singleSignOn.sso') as string}
                actions={[
                  {
                    icon: canEdit ?
                      <Tooltip title={translate('common.edit')}><Icon icon={faPencil}/></Tooltip>:undefined,
                    label: translate('common.edit'),
                    onClick: canEdit ? editSSO:undefined
                  }
                ]}
              />
            }
            content={
              <CardContent>
                <Translate id='singleSignOn.ssoDesc'/>
                <div className={styles.status}>
                  <p><Translate id='common.status'/></p>
                  <Tag type={ssoSamlEnabled ? TagType.ACTIVE:TagType.INACTIVE}><Translate
                    id={ssoSamlEnabled ? 'common.statuses.enabled':'common.statuses.disabled'}/> </Tag>
                </div>
              </CardContent>
            }
          />

          <Card
            size={CardSizeTypes.LARGE}
            header={
              <CardHeader
                label={translate('common.form.singleSignOnOidc') as string}
                actions={[
                  {
                    icon: canEdit ?
                      <Tooltip title={translate('common.edit')}><Icon icon={faPencil}/></Tooltip>:undefined,
                    label: translate('common.edit'),
                    onClick: canEdit ? handleActionOIDC:undefined
                  }
                ]}
              />
            }
            content={
              <CardContent>
                <Translate id='singleSignOn.openIdDesc'/>
                <div className={styles.status}>
                  <p><Translate id='common.status'/></p>
                  <Tag
                    type={oidcEnabled ? TagType.ACTIVE:TagType.INACTIVE}><Translate
                    id={oidcEnabled ? 'common.statuses.enabled':'common.statuses.disabled'}/>
                  </Tag>
                </div>
              </CardContent>
            }
          />

          {/* Temp don't render on account details page until redesign and additional testing is done */}
          {/* {accountDetailsPage && */}
          {/*   <Card */}
          {/*     size={CardSizeTypes.LARGE} */}
          {/*     header={ */}
          {/*       <CardHeader */}
          {/*         label={translate('clientAuthCertLoginUpdate.cardHeadline') as string} */}
          {/*         actions={[ */}
          {/*           { */}
          {/*             icon: canEdit ? <Tooltip title={translate('common.edit')}><Icon icon={faPencil}/></Tooltip> : undefined, */}
          {/*             label: translate('common.edit'), */}
          {/*             onClick: canEdit ? handleActionUpdateClientAuthCertLogIn : undefined */}
          {/*           } */}
          {/*         ]} */}
          {/*       /> */}
          {/*     } */}
          {/*     content={ */}
          {/*       <CardContent> */}
          {/*         <Translate id='clientAuthCertLoginUpdate.cardDescription'/> */}
          {/*         <div className={styles.status}> */}
          {/*           <p><Translate id='common.status'/></p> */}
          {/*           <Tag type={clientAuthCertLoginEnabled ? TagType.ACTIVE : TagType.INACTIVE}> */}
          {/*             <Translate id={ clientAuthCertLoginEnabled !== undefined && clientAuthCertLoginEnabled ? 'common.statuses.enabled' : 'common.statuses.disabled'} /> */}
          {/*           </Tag> */}
          {/*         </div> */}
          {/*       </CardContent> */}
          {/*     } */}
          {/*   /> */}
          {/* } */}

          <SideRail
            className={cn(styles.sideRail, showSideRail && styles.expanded)}
            headline={<h2>{loadSideRailTitle(sideRailContent)}</h2>}
            show={showSideRail}
            onDismiss={() => setShowSideRail(!showSideRail)}
            lightbox
          >
            <div className={styles.sideRailContent}>
              {loadSideRailContent(sideRailContent)}
            </div>
          </SideRail>
        </>
      </CardContainer>
    </div>
  )
}

export default connect(
  null,
  { fetchTenantById }
)(SignInSettings)
